/**
 * Módulo Configurações: a casca com as sete abas e o conteúdo de cada uma
 * (Preferências, Notificações, Conta, Assinatura, Exportar, Privacidade).
 *
 * A aba Organização não está aqui: ela renderiza `OrgSettingsPanel`, que é
 * outro componente e vira outra fatia. Só o rótulo dela entra em `nav`.
 *
 * Os textos foram RECORTADOS dos componentes sem reescrita. Onde a tela dizia
 * "Salvar Alterações" numa aba e "Salvar Configurações" em outra, o dicionário
 * repete os dois: uniformizar aqui seria mudar produto no meio de uma tradução.
 *
 * Onde o componente tinha uma lista de opções, só o RÓTULO veio para cá. O
 * `value` continua no componente porque é ele que o `switch` enxerga, que o
 * localStorage guarda e que a coluna do banco recebe: `reminder_days` é o
 * número de dias, `email_summary` é 'never'/'daily'/'weekly', o tema é
 * 'light'/'dark'/'system' e o módulo inicial é o ID de rota.
 */
const settings = {
  /** Casca do módulo: título e as abas da coluna esquerda. */
  nav: {
    title: 'Configurações',
    sheetTitle: 'Seções',
    tabs: {
      preferences: 'Preferências',
      notifications: 'Notificações',
      account: 'Conta',
      billing: 'Assinatura',
      export: 'Exportar',
      privacy: 'Privacidade',
      organization: 'Organização',
    },
  },

  /** Aba Conta: perfil, segurança e a zona de exclusão de conta. */
  account: {
    title: 'Conta e Perfil',
    subtitle: 'Gerencie suas informações pessoais e segurança',

    personalSection: 'Informações Pessoais',
    name: 'Nome',
    nameHint: 'Seu nome visível para outros membros da equipe',
    namePlaceholder: 'Seu nome',
    whatsapp: 'WhatsApp',
    whatsappHint: 'Usamos para te ajudar a começar e ouvir seu feedback',
    /**
     * Máscara de telefone BRASILEIRO, igual nos três idiomas: o campo é
     * validado por `isValidPhoneBR` e só aceita número do Brasil, então mostrar
     * um formato local (+1, +52) ensinaria o usuário a digitar o que a
     * validação recusa.
     */
    whatsappPlaceholder: '(11) 98765-4321',
    email: 'Email',
    emailHint: 'Email usado para login (não pode ser alterado)',

    securitySection: 'Segurança',
    password: 'Senha',
    passwordHint: 'Altere sua senha de acesso ao sistema',
    changePassword: 'Alterar senha',

    invalidPhone: 'Informe um WhatsApp válido com DDD, ex.: (11) 98765-4321.',
    saved: 'Perfil atualizado com sucesso!',
    saveError: 'Erro ao atualizar perfil.',
    saving: 'Salvando...',
    save: 'Salvar Alterações',

    deletionCanceled: 'Exclusão de conta cancelada com sucesso!',
    deletionCancelError: 'Erro ao cancelar exclusão',
    /** `{{date}}` já vem formatado por `fmt.date`, no formato do idioma. */
    deletionBanner: 'Sua conta será excluída em {{date}}',
    deletionBannerHint: 'Todos os seus dados serão permanentemente removidos após esta data.',
    cancelDeletion: 'Cancelar exclusão',

    dangerSection: 'Zona de Perigo',
    deleteAccount: 'Excluir minha conta',
    deleteAccountHint: 'Remove permanentemente sua conta e todos os dados após 30 dias. Esta ação pode ser cancelada durante o período de carência.',
    deleteRequested: 'Exclusão solicitada',
    deleteButton: 'Excluir conta',
  },

  /** Aba Assinatura: plano atual, uso, datas de cobrança e features do plano. */
  billing: {
    loading: 'Carregando informações...',
    title: 'Assinatura',
    subtitle: 'Gerencie seu plano e informações de pagamento',
    portalError: 'Erro ao abrir gerenciamento de assinatura. Tente novamente.',

    currentPlanSection: 'Plano Atual',
    /** `{{plan}}` é o nome comercial (Free, Starter...), que não se traduz. */
    planBadge: 'Plano {{plan}}',
    trialPrice: 'Teste Gratuito',
    freePrice: 'Gratuito',
    perUserMonth: '/usuário/mês',
    billedYearly: '(cobrado anualmente)',

    /**
     * Rótulos do status da assinatura. A chave é o status do Stripe
     * ('active', 'trialing', 'past_due', 'canceled'), comparado no componente.
     */
    statusActive: 'Ativo',
    statusTrial: 'Trial',
    /** `{{count}}` = dias que ainda restam do teste. */
    statusTrialDays: 'Trial — {{count}} dias restantes',
    statusPastDue: 'Pagamento pendente',
    statusCanceled: 'Cancelado',

    upgrade: 'Fazer Upgrade',
    viewPlans: 'Ver Planos',
    manage: 'Gerenciar Assinatura',

    trialExpiredTitle: 'Seu período de teste expirou',
    trialExpiredBody: 'Faça upgrade para continuar usando todos os recursos do Professional.',

    usageSection: 'Uso Atual',
    projects: 'Projetos',
    projectsHint: 'Quantidade de projetos criados',
    attachments: 'Anexos',
    attachmentsHint: 'Espaco usado por arquivos anexados nas tarefas dos seus projetos',

    capacityLockedTitle: 'Dados de Planejamento de Capacidade',
    capacityLockedBody: 'Você possui recursos e alocações cadastrados que não estão acessíveis no plano atual. Faça upgrade para o Professional para acessá-los novamente.',

    detailsSection: 'Detalhes da Assinatura',
    nextCharge: 'Próxima cobrança',
    nextChargeHint: 'Data do próximo pagamento',
    /** Frase partida porque a data fica em negrito no meio dela. */
    trialEndsPre: 'Seu período de teste gratuito expira em ',
    trialEndsPost: ' — adicione um método de pagamento para continuar usando',
    /** `{{date}}` já vem formatado por `fmt.date`. */
    cancelAtPeriodEnd: 'Sua assinatura será cancelada em {{date}}',

    featuresSection: 'Recursos do seu plano',
    /**
     * Rótulo de cada feature do plano. A CHAVE é o identificador que vem do
     * banco (coluna `features` do plano) e nunca muda com o idioma; feature
     * sem entrada aqui cai no fallback do componente.
     */
    features: {
      gantt_basic: 'Gantt Básico',
      gantt_dependencies: 'Dependências no Gantt',
      calendar: 'Calendário',
      timeline: 'Cronograma/Timeline',
      todos: 'Pendências',
      risks: 'Módulo de Riscos',
      changes: 'Módulo de Mudanças',
      costs: 'Módulo de Custos',
      export_csv: 'Exportar CSV',
      export_csv_limited: 'Exportar CSV (limitado)',
      export_pdf: 'Exportar PDF',
      export_png: 'Exportar PNG',
      share_public: 'Compartilhamento Público',
      share_password: 'Compartilhamento com Senha',
      api_access: 'Acesso à API',
      portfolio: 'Portfólio de Projetos',
      capacity_planning: 'Planejamento de Capacidade',
      raci_matrix: 'Matriz RACI',
      eap_wbs: 'EAP (Estrutura Analítica)',
      comments: 'Comentários e @Menções',
      organizations: 'Organizações',
      import_external: 'Importar de Outros Sistemas',
      multi_assignee: 'Múltiplos Responsáveis',
      project_templates: 'Templates de Projeto',
      schedule_baseline: 'Baseline de Cronograma',
      critical_path: 'Caminho Crítico',
      ai_project_creation: 'Criação de Projeto com IA',
    },
  },

  /** Aba Exportar: exportação de projeto, backup completo e importação. */
  export: {
    title: 'Exportar e Importar',
    subtitle: 'Faça backup dos seus dados ou exporte para outras ferramentas',

    currentSection: 'Exportar Projeto Atual',
    /** `{{name}}` = nome do projeto aberto. */
    currentProject: 'Projeto: {{name}}',
    noProject: 'Nenhum projeto selecionado',
    /** Nome-base do arquivo baixado quando o projeto está sem nome. */
    fileFallback: 'projeto',

    excelTitle: 'Excel (XLSX)',
    excelHint: 'Planilha com abas de tarefas, baseline, equipe, sprints e curva S',
    excelLocked: 'Exporte a planilha completa do projeto',
    exportedExcel: 'Planilha gerada com sucesso!',

    csvTitle: 'CSV',
    csvHint: 'Lista de tarefas em texto, para reimportar em outra ferramenta',
    /** `{{count}}` = exportações CSV que ainda restam no mês. */
    csvRemaining: '{{count}} exportação restante este mês',
    csvLimitReached: 'Limite mensal atingido',
    csvLocked: 'Limite atingido. Faça upgrade para ilimitado.',

    jsonTitle: 'JSON',
    jsonHint: 'Backup completo do projeto em formato JSON',

    pdfTitle: 'PDF / PNG',
    /** Frase partida: "Exportar" é o nome do botão e vai em negrito. */
    pdfHintPre: 'Use o botão ',
    pdfHintStrong: 'Exportar',
    pdfHintPost: ' no cabeçalho do Gantt para exportar PDF ou imagem',
    pdfLockedHint: 'Exporte o gráfico de Gantt em PDF ou imagem',
    /** `{{plan}}` = nome comercial do plano mínimo, que não se traduz. */
    planBadge: 'Plano {{plan}}',

    backupSection: 'Backup Completo',
    allProjects: 'Todos os Projetos',
    /** `{{count}}` = quantos projetos o usuário tem. */
    allProjectsHint: 'Backup de todos os seus {{count}} projetos',

    importSection: 'Importar',
    importJson: 'Importar backup JSON',
    importJsonHint: 'Restaure projetos a partir de um arquivo de backup',
    selectFile: 'Selecionar arquivo',
    importExternal: 'Importar de outro sistema',
    importExternalHint: 'Importe projetos do Trello, ClickUp, Azure DevOps ou MS Project',
    importButton: 'Importar',

    /**
     * Primeira linha do CSV gerado. É conteúdo que o usuário abre no Excel, não
     * contrato de importação: nada no app lê esses cabeçalhos de volta.
     */
    csvHeaders: ['Nome', 'Tipo', 'Data Início', 'Data Fim', 'Duração', 'Progresso', 'Responsável'],
    /** Valor da coluna Tipo quando a tarefa não tem tipo definido. */
    csvDefaultType: 'tarefa',

    exported: 'Exportado com sucesso!',
    exportedCsv: 'Exportado para CSV com sucesso!',
    exportError: 'Erro ao exportar.',

    fileTooLarge: 'Arquivo muito grande. Limite: 10MB',
    /** `{{name}}` = nome do projeto que acabou de entrar. */
    imported: 'Projeto "{{name}}" importado com sucesso!',
    multiNotSupported: 'Importação de múltiplos projetos será disponibilizada em breve. Use o backup de projeto individual.',
    planLimit: 'Limite de projetos do seu plano atingido. Faça upgrade para importar.',
    /** `{{message}}` = um dos erros de validação abaixo. */
    importError: 'Erro ao importar: {{message}}',
    invalidFile: 'arquivo inválido.',

    /** Erros de validação do arquivo; chegam à tela dentro de `importError`. */
    invalidFormat: 'Formato inválido',
    missingVersion: 'Versão ausente',
    missingExportDate: 'Data de exportação ausente',
    invalidProjectName: 'Nome de projeto inválido',
    tooManyTasks: 'Limite de 5000 tarefas por importação',
    unknownFormat: 'Formato de arquivo não reconhecido',
  },

  /** Aba Notificações: alertas no app, lembretes e email. */
  notifications: {
    title: 'Notificações',
    subtitle: 'Configure como e quando você deseja ser notificado',

    appSection: 'Alertas no Aplicativo',
    mention: 'Menções',
    mentionHint: 'Quando alguém mencionar você em um comentário',
    comment: 'Comentários',
    commentHint: 'Quando alguém comentar em uma tarefa sua',
    assigned: 'Atribuições',
    assignedHint: 'Quando você for atribuído a uma tarefa',
    taskStatus: 'Mudanças de status',
    taskStatusHint: 'Quando o status de uma tarefa sua for alterado',
    overdue: 'Tarefas atrasadas',
    overdueHint: 'Receber alerta quando uma tarefa passar do prazo',

    reminder: 'Lembrete de vencimento',
    reminderHint: 'Quantos dias antes do vencimento você quer ser lembrado',
    selectPlaceholder: 'Selecione',
    /**
     * Rótulos do lembrete, indexados pelo NÚMERO DE DIAS que vai para a coluna
     * `reminder_days`. O 7 aparece como "1 semana" só na tela.
     */
    reminderDays: {
      '1': '1 dia antes',
      '2': '2 dias antes',
      '3': '3 dias antes',
      '5': '5 dias antes',
      '7': '1 semana antes',
    },

    browser: 'Notificações do navegador',
    browserHint: 'Receber notificações push no navegador',
    browserEnable: 'Ativar',
    browserUnsupported: 'Neste navegador não é possível. No iPhone, instale o MeuGantt na tela de início.',

    emailSection: 'Email',
    emailMention: 'Menções por email',
    emailMentionHint: 'Receber email quando alguém mencionar você',
    emailComment: 'Comentários por email',
    emailCommentHint: 'Receber email quando comentarem em uma tarefa sua',
    emailAssigned: 'Atribuições por email',
    emailAssignedHint: 'Receber email quando for atribuído a uma tarefa',
    emailStatus: 'Mudanças de status por email',
    emailStatusHint: 'Receber email quando o status de uma tarefa sua mudar',
    emailDeadline: 'Prazos por email',
    emailDeadlineHint: 'Receber email quando um prazo estiver se aproximando',

    emailSummary: 'Resumo por email',
    emailSummaryHint: 'Receba um resumo das suas tarefas e projetos por email',
    /** Indexado pelo valor salvo na coluna `email_summary`. */
    summaryOptions: {
      never: 'Nunca',
      daily: 'Diariamente',
      weekly: 'Semanalmente',
    },
    saving: 'Salvando...',
    saved: 'Salvo!',
    save: 'Salvar Configurações',
  },

  /** Aba Preferências: comportamento e aparência. O seletor de idioma vem de `common.language`. */
  preferences: {
    title: 'Preferências',
    subtitle: 'Personalize o comportamento do aplicativo',

    behaviorSection: 'Comportamento',
    defaultModule: 'Módulo inicial',
    defaultModuleHint: 'Qual tela será exibida ao abrir um projeto',
    selectPlaceholder: 'Selecione',
    /**
     * Rótulos do módulo inicial, indexados pelo ID do módulo — o mesmo que vai
     * para o localStorage (`current-module`) e para o roteamento.
     */
    modules: {
      home: 'Início',
      gantt: 'Gantt',
      schedule: 'Cronograma',
      calendar: 'Calendário',
      todos: 'Pendências',
      risks: 'Riscos',
      changes: 'Mudanças',
      costs: 'Custos',
    },

    sidebarCollapsed: 'Sidebar recolhida',
    sidebarCollapsedHint: 'Iniciar com a barra lateral recolhida por padrão',

    appearanceSection: 'Aparência',
    theme: 'Tema',
    themeHint: 'Escolha o tema visual do aplicativo',
    /** Indexado pelo valor que o ThemeContext persiste. */
    themes: {
      light: 'Claro',
      dark: 'Escuro',
      system: 'Sistema',
    },

    saving: 'Salvando...',
    saved: 'Salvo!',
    save: 'Salvar Preferências',
  },

  /**
   * Aba Privacidade. A LGPD é a lei brasileira e continua sendo a lei aplicável
   * em qualquer idioma da interface: a sigla e o artigo ficam, só o texto ao
   * redor é traduzido.
   */
  privacy: {
    title: 'Privacidade e Proteção de Dados',
    subtitle: 'Gerencie seus dados pessoais conforme a Lei Geral de Proteção de Dados (LGPD)',

    rightsSection: 'Seus Direitos (Art. 18 - LGPD)',
    rightsIntro: 'Como titular de dados pessoais, você tem direito a:',
    /** Cada item vira "<termo em negrito> — descrição". */
    rights: [
      { term: 'Acesso', desc: 'solicitar cópia dos dados pessoais que armazenamos' },
      { term: 'Correção', desc: 'corrigir dados incompletos ou desatualizados' },
      { term: 'Eliminação', desc: 'solicitar a exclusão dos seus dados pessoais' },
      { term: 'Portabilidade', desc: 'exportar seus dados em formato estruturado' },
      { term: 'Revogação', desc: 'retirar seu consentimento a qualquer momento' },
      { term: 'Informação', desc: 'saber com quem seus dados são compartilhados' },
    ],

    actionsSection: 'Ações Disponíveis',
    exportData: 'Solicitar meus dados',
    exportDataHint: 'Baixe uma cópia completa dos seus dados pessoais em formato JSON',
    exporting: 'Exportando...',
    exportButton: 'Exportar dados',
    /** Nome-base do JSON baixado; a data é anexada pelo componente. */
    exportFileName: 'meugantt_meus_dados',
    exported: 'Dados exportados com sucesso!',
    exportError: 'Erro ao exportar dados',

    deleteAccount: 'Excluir minha conta',
    deleteAccountHint: 'Remove permanentemente sua conta e dados após período de carência de 30 dias',
    deleteButton: 'Excluir conta',

    policy: 'Política de Privacidade',
    policyHint: 'Leia nossa política completa sobre coleta, uso e proteção de dados',
    policyButton: 'Ver política',

    /** Termina sem ponto: o email vem logo depois, como link. */
    dpoContact: 'Para exercer qualquer direito previsto na LGPD ou esclarecer dúvidas sobre seus dados pessoais, entre em contato pelo email',
  },
};

/** en e es derivam desta forma: chave faltando vira erro de compilação. */
export type SettingsCopy = typeof settings;

export default settings;
